// src/cockpit4/components/StrategicAlignment.tsx
// ============================================
// 战略对齐（项目中心 P2-功能9）- v2.0 详细改进计划
// 项目与战略目标关联映射 / 战略贡献度计算 / 优先级保障 / 进度追踪 / 影响分析
// 数据来源：localStorage（xeaos_strategic_alignment）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Target, GitBranch, TrendingUp, AlertTriangle } from "lucide-react";

interface StrategicGoal {
  id: string;
  name: string;
  weight: number;
  progress: number;
  projects: { name: string; contribution: number }[];
}

const INITIAL_GOALS: StrategicGoal[] = [
  { id: "g1", name: "打造 AI 经营团队标杆产品", weight: 40, progress: 68, projects: [{ name: "XEAOS 核心系统", contribution: 60 }, { name: "AI 营销增长", contribution: 40 }] },
  { id: "g2", name: "建立知识驱动增长飞轮", weight: 25, progress: 55, projects: [{ name: "知识资产平台", contribution: 80 }, { name: "客户成功体系", contribution: 20 }] },
  { id: "g3", name: "实现多业务线协同变现", weight: 20, progress: 42, projects: [{ name: "量化交易系统", contribution: 50 }, { name: "AI 营销增长", contribution: 50 }] },
  { id: "g4", name: "构建可持续的客户生态", weight: 15, progress: 60, projects: [{ name: "客户成功体系", contribution: 70 }, { name: "知识资产平台", contribution: 30 }] },
];

const KEY = "xeaos_strategic_alignment";

function load(): StrategicGoal[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as StrategicGoal[];
  } catch { /* ignore */ }
  return INITIAL_GOALS;
}

export default function StrategicAlignment() {
  const [goals, setGoals] = useState<StrategicGoal[]>(load);

  const stats = useMemo(() => {
    const weightedProgress = goals.reduce((a, g) => a + g.progress * g.weight, 0) / goals.reduce((a, g) => a + g.weight, 0);
    const atRisk = goals.filter((g) => g.progress < 50).length;
    return { weightedProgress, atRisk };
  }, [goals]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Target size={14} className="text-amber-400" />
        <h3 className="text-[12px] font-medium text-white">战略对齐</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Strategic Alignment</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="战略整体进度" value={`${stats.weightedProgress.toFixed(0)}%`} color="#34D399" />
        <MetricCard label="风险战略" value={`${stats.atRisk} 项`} color="#F87171" />
        <MetricCard label="战略目标" value={`${goals.length} 项`} color="#38BDF8" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch size={11} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">战略目标 → 项目关联映射</span>
        </div>
        <div className="space-y-2">
          {goals.map((g) => (
            <div key={g.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{g.name}</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">权重 {g.weight}%</span>
                <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${g.progress < 50 ? "bg-red-500/15 text-red-400" : g.progress < 70 ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{g.progress}%</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${g.progress}%`, background: g.progress < 50 ? "#F87171" : g.progress < 70 ? "#FBBF24" : "#34D399" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-500">{g.projects.length} 个项目支撑</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {g.projects.map((p, i) => (
                  <span key={i} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-white/[0.06] text-gray-400">{p.name} · 贡献 {p.contribution}%</span>
                ))}
              </div>
              {g.progress < 50 && (
                <div className="flex items-start gap-1.5 mt-1.5 text-[8px] font-mono text-red-300/80">
                  <AlertTriangle size={8} className="mt-0.5" /> 进度滞后预警：建议召开战略对齐会，调整资源分配
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">战略执行影响分析</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { color: "#34D399", text: "「XEAOS 核心系统」对整体战略贡献 32%，建议优先保障资源" },
            { color: "#FBBF24", text: "「量化交易系统」战略贡献有限（18%）且风险高 → 建议放缓" },
            { color: "#38BDF8", text: "「知识资产平台」对知识飞轮战略贡献 80% → 建议加码" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
              <span className="text-gray-400 leading-relaxed">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

